import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, User, FileSignature, UserCog, Pencil, FileText, RotateCcw, Trash2, Activity, Download, FileSpreadsheet } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  created:        { icon: FileText,      color: 'bg-blue-100 text-blue-700 border-blue-200',         label: i18n.t('auto.lbl.created') },
  signed:         { icon: FileSignature, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: i18n.t('auto.lbl.signed') },
  reassigned:     { icon: UserCog,       color: 'bg-amber-100 text-amber-700 border-amber-200',      label: i18n.t('auto.lbl.reassigned') },
  updated:        { icon: Pencil,        color: 'bg-slate-100 text-slate-700 border-slate-200',      label: i18n.t('auto.lbl.edited') },
  edited:         { icon: Pencil,        color: 'bg-slate-100 text-slate-700 border-slate-200',      label: i18n.t('auto.lbl.edited') },
  reverted:       { icon: RotateCcw,     color: 'bg-orange-100 text-orange-700 border-orange-200',   label: i18n.t('auto.lbl.reverted') },
  status_changed: { icon: Activity,      color: 'bg-purple-100 text-purple-700 border-purple-200',   label: i18n.t('auto.lbl.status') },
  deleted:        { icon: Trash2,        color: 'bg-red-100 text-red-700 border-red-200',            label: i18n.t('auto.lbl.deleted') },
};

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16).replace('T', ' '); }
}

/**
 * Per-document audit trail. Renders a vertical timeline of events
 * (created / signed / reassigned / edited) returned by the backend.
 *
 * Props:
 *   - kind: 'test-report' | 'checklist'
 *   - instanceId: string
 *   - reloadKey?: any — bump to force refetch (e.g. after sign / reassign)
 */
export default function AuditTrailPanel({ kind, instanceId, reloadKey = 0 }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instanceId) return;
    let canceled = false;
    setLoading(true); setError(null);
    const path = kind === 'checklist'
      ? `${API}/checklists/instances/${instanceId}/audit`
      : `${API}/test-reports/instances/${instanceId}/audit`;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    axios.get(path, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!canceled) setEvents(r.data || []); })
      .catch(e => { if (!canceled) setError(e.response?.data?.detail || 'Failed to load history'); })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
  }, [kind, instanceId, reloadKey]);

  const exportAudit = async (fmt) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const url = `${API}/audit/export.${fmt}?doc_id=${encodeURIComponent(instanceId)}`;
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = weekFileName(`audit_log_${instanceId.slice(0, 8)}.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('Audit export failed:', e);
    }
  };

  return (
    <Card data-testid="audit-trail-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-500" /> {t('auto.iso.history')}
          <span className="text-xs text-gray-400 font-normal">
            ({events.length} event{events.length !== 1 ? 's' : ''})
          </span>
        </CardTitle>
        {events.length > 0 && (
          <DropdownMenu>
            <DocLangToggle testId="doc-lang-audittrailpanel-0" />
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="audit-export-menu">
                <Download className="h-3.5 w-3.5" /> {t('auto.iso.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportAudit('xlsx')} data-testid="audit-export-xlsx">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> {t('auto.iso.excelXlsx')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAudit('pdf')} data-testid="audit-export-pdf">
                <FileText className="h-4 w-4 mr-2" /> {t('auto.iso.pdf')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        {loading && <div className="text-xs text-gray-400">{t('auto.iso.loading')}</div>}
        {error && <div className="text-xs text-red-600">{error}</div>}
        {!loading && !error && events.length === 0 && (
          <div className="text-xs text-gray-400">{t('auto.iso.noEventsYet')}</div>
        )}
        {!loading && !error && events.length > 0 && (
          <ol className="relative border-l border-gray-200 ml-2 space-y-3">
            {events.map((e, idx) => {
              const meta = KIND_META[e.kind] || KIND_META.updated;
              const Icon = meta.icon;
              return (
                <li key={`${e.kind}-${e.at}-${idx}`} className="ml-3" data-testid={`audit-event-${idx}`}>
                  <span className={`absolute -left-[11px] flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white ${meta.color}`}>
                    <Icon className="h-2.5 w-2.5" />
                  </span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{e.label}</span>
                    {e.detail && <span className="text-xs text-gray-500">— {e.detail}</span>}
                  </div>
                  {e.changes && e.changes.length > 0 && (
                    <ul
                      className="mt-1 space-y-0.5 text-[11px] text-gray-600 font-mono bg-gray-50 border border-gray-100 rounded px-2 py-1"
                      data-testid={`audit-event-${idx}-changes`}
                    >
                      {e.changes.map((line, ci) => (
                        <li key={ci} className="leading-snug">{line}</li>
                      ))}
                    </ul>
                  )}
                  <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {e.actor_name || 'system'}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span title={e.at}>{formatTs(e.at)}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
